import { useMemo, type ReactNode } from "react";
import { MapPin, type LucideIcon } from "lucide-react";
import { renderDisplayText } from "@/lib/utils";
import { isAwaitingFirstResult, useTenantOrgId } from "@/hooks/useTenantOrgId";
import { useHeroContent, DEFAULT_HERO_BADGES } from "@/hooks/useHeroContent";
import { useCustomerReviews, type ReviewItem } from "@/hooks/useCustomerReviews";
import { usePublicGoogleReviews, type GoogleLivePlace, type GoogleLiveReview } from "@/hooks/useGoogleReviews";
import { useCompanyInfoFull } from "@/hooks/useCompanyInfo";
import { useTenantPath } from "@/hooks/useTenantPath";
import { useDemoShowcase } from "@/hooks/useDemoShowcase";
import { useOrgSettings } from "@/hooks/useOrgSettings";
import { useHomepageFeaturedCategories, useHomepageServicePricing } from "@/hooks/useHomepageServices";
import { getIconComponent } from "@/components/admin/icon-map";
import { formatAreas } from "@/lib/service-areas";
import { formatMoney, parseMoneyValue } from "@/lib/currency";
import heroImageDefault from "@/assets/hero-cleaner.jpg";
import cleanLivingRoom from "@/assets/clean-living-room.jpg";
import cleanKitchen from "@/assets/clean-kitchen.jpg";
import cleanOffice from "@/assets/clean-office.jpg";
import resultsBathroom from "@/assets/results-bathroom.jpg";

/**
 * Shared data layer for the homepage layout variants. Every variant renders the
 * same content from these hooks, so switching theme only changes composition.
 */

const HIGHLIGHT = "regular cleaning";
const DEFAULT_SERVICE_IMAGES = [cleanLivingRoom, cleanKitchen, cleanOffice, resultsBathroom];

export interface TrustItem {
  key: string;
  Icon: LucideIcon;
  title: string;
  subtitle: string;
}

export interface HeroModel {
  ready: boolean;
  heroPending: boolean;
  reviewsPending: boolean;
  eyebrow: string;
  title: string;
  titleNode: ReactNode;
  subtitle: string;
  ctaText: string;
  heroImage: string;
  /** Service photos, used by collage-style heroes. Always at least 3 entries. */
  gallery: string[];
  rating: number;
  ratingLabel: string;
  reviewCount: number;
  ratingLine: string;
  areas: string[];
  bookingHref: string;
  servicesHref: string;
  trust: TrustItem[];
}

export function useTrustItems(): { pending: boolean; items: TrustItem[] } {
  const orgId = useTenantOrgId();
  const heroQuery = useHeroContent(orgId);
  const companyQuery = useCompanyInfoFull(orgId);
  const demo = useDemoShowcase();
  const pending = isAwaitingFirstResult(heroQuery) || isAwaitingFirstResult(companyQuery);
  const hero = heroQuery.data;
  const areas = companyQuery.data?.service_areas ?? [];
  const defaultBadges = demo ? demo.hero.badges : DEFAULT_HERO_BADGES;
  const badges = (hero?.badges?.length && !hero.isDefault ? hero.badges : defaultBadges).slice(0, 3);
  const items: TrustItem[] = [
    ...badges.map((badge, index) => ({
      key: `${badge.title}-${index}`,
      Icon: getIconComponent(badge.icon || "Star"),
      title: badge.title,
      subtitle: badge.subtitle,
    })),
    ...(areas.length > 0
      ? [{
          key: "areas",
          Icon: MapPin,
          title: "Local team",
          subtitle: areas.length > 3 ? `Covering ${areas.slice(0, 3).join(", ")} + more` : `Covering ${areas.join(", ")}`,
        }]
      : []),
  ];
  return { pending, items };
}

export function useHeroModel(): HeroModel {
  const orgId = useTenantOrgId();
  const tenantPath = useTenantPath();
  const heroQuery = useHeroContent(orgId);
  const content = heroQuery.data;
  const heroPending = isAwaitingFirstResult(heroQuery);
  const reviewsQuery = useCustomerReviews(orgId);
  const reviews = reviewsQuery.data ?? [];
  const reviewsPending = isAwaitingFirstResult(reviewsQuery);
  const companyQuery = useCompanyInfoFull(orgId);
  const companyPending = isAwaitingFirstResult(companyQuery);
  const demo = useDemoShowcase();
  const demoHero = demo && (!content || content.isDefault) ? demo.hero : null;
  const featured = useHomepageFeaturedCategories(orgId).data ?? [];
  const trust = useTrustItems().items;

  const title = demoHero?.title ?? content?.title ?? "One-off or regular cleaning";
  const subtitle = demoHero?.subtitle ?? content?.subtitle ?? "We provide reliable, quality cleaning for busy households and businesses, with clear, upfront pricing so you always know exactly what you're paying for. It's a straightforward service designed to keep your space looking its best without any hidden surprises.";
  const ctaText = demoHero?.ctaText ?? content?.ctaText ?? "Book my cleaning";
  const areas = companyQuery.data?.service_areas ?? [];
  const eyebrow = demo
    ? demo.hero.eyebrow
    : areas.length > 0 ? `Trusted cleaners in ${formatAreas(areas)}` : "Professional cleaning";
  const rating = reviews.length > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : 5;
  const ratingLabel = rating >= 4.5 ? "Excellent" : rating >= 4 ? "Great" : "Rated";
  const ratingLine = reviews.length > 0
    ? `${rating.toFixed(1)} from ${reviews.length} customer review${reviews.length === 1 ? "" : "s"}`
    : demo ? demo.hero.ratingLine : "Loved by local customers";

  const titleNode: ReactNode = title.includes(HIGHLIGHT)
    ? (() => {
        const [before, after] = title.split(HIGHLIGHT);
        return (
          <>
            {renderDisplayText(before)}
            <span className="tp-highlight">{HIGHLIGHT}</span>
            {renderDisplayText(after)}
          </>
        );
      })()
    : renderDisplayText(title);

  const heroImage = content?.imageUrl || heroImageDefault;
  const gallery = [0, 1, 2].map((i) => featured[i]?.imageUrl || DEFAULT_SERVICE_IMAGES[i]);

  return {
    ready: !heroPending && !companyPending,
    heroPending,
    reviewsPending,
    eyebrow,
    title,
    titleNode,
    subtitle,
    ctaText,
    heroImage,
    gallery,
    rating,
    ratingLabel,
    reviewCount: reviews.length,
    ratingLine,
    areas,
    bookingHref: tenantPath("/booking"),
    servicesHref: tenantPath("/services"),
    trust,
  };
}

/* ---------------------------------- Services --------------------------------- */

export interface ServiceCardItem {
  key: string;
  category: string;
  title: string;
  description: string;
  price: string;
  image: string;
}

export interface ServicesModel {
  loading: boolean;
  items: ServiceCardItem[];
  title: string;
  lead: string;
  servicesHref: string;
}

export function useServicesModel(): ServicesModel {
  const orgId = useTenantOrgId();
  const tenantPath = useTenantPath();
  const demo = useDemoShowcase();
  const categoriesQuery = useHomepageFeaturedCategories(orgId);
  const { data: orgSettings } = useOrgSettings(orgId);
  const { data: featuredCategories = [] } = categoriesQuery;
  const pricingQuery = useHomepageServicePricing(orgId);
  const { data: servicesData = [] } = pricingQuery;
  const loading = isAwaitingFirstResult(categoriesQuery) || isAwaitingFirstResult(pricingQuery);

  const items = useMemo<ServiceCardItem[]>(() => {
    const prices: Record<string, number[]> = {};
    servicesData.forEach((s) => {
      if (!s.category) return;
      if (!prices[s.category]) prices[s.category] = [];
      const n = parseMoneyValue(s.price);
      // Quote-only services (price 0) mustn't drag the "from" price down to £0.
      if (n !== null && n > 0) prices[s.category].push(n);
    });
    const categoryNames = Object.keys(prices);
    const prefs = new Map(featuredCategories.map((f) => [f.category, f]));
    const ordered: string[] = [];
    featuredCategories.forEach((f) => {
      if (categoryNames.includes(f.category) && !ordered.includes(f.category)) ordered.push(f.category);
    });
    categoryNames.forEach((c) => {
      if (!ordered.includes(c)) ordered.push(c);
    });
    return ordered.map((category, index) => {
      const pref = prefs.get(category);
      const list = prices[category];
      return {
        key: category,
        category,
        title: pref?.title || category,
        description: pref?.description || "",
        price: list.length > 0 ? formatMoney(Math.min(...list), orgSettings?.currency, { from: true }) : "Contact for quote",
        image: pref?.imageUrl || DEFAULT_SERVICE_IMAGES[index % DEFAULT_SERVICE_IMAGES.length],
      };
    });
  }, [servicesData, featuredCategories, orgSettings?.currency]);

  return {
    loading,
    items,
    title: demo?.homeServices.title ?? "Our Services",
    lead: demo?.homeServices.lead ?? "Professional cleaning solutions for homes, offices, and everything in between",
    servicesHref: tenantPath("/services"),
  };
}

/* ---------------------------------- Reviews ---------------------------------- */

const DEFAULT_REVIEWS: ReviewItem[] = [
  {
    name: "James M.",
    location: "Cardiff Bay",
    content: "We use The Polished Version for all 5 of our AirBnB properties. The turnaround is fast, the quality is consistent, and our guests always mention how spotless the places are.",
    rating: 5,
  },
  {
    name: "Emma W.",
    location: "Newport City Centre",
    content: "Easy to book online and the cleaner arrived right on time. My kitchen and bathrooms have never looked better. Will definitely be booking regularly!",
    rating: 5,
  },
];

export interface ReviewCardItem {
  key: string;
  quote: string;
  name: string;
  /** Location, or the relative time ("3 weeks ago") for Google reviews. */
  meta: string;
  rating: number;
  photoUrl?: string | null;
  google?: { authorUrl: string | null; reviewUrl: string };
}

export interface ReviewsModel {
  pending: boolean;
  items: ReviewCardItem[];
  average: number;
  label: string;
  countLine: string;
  subtitle: string;
  google: { place: GoogleLivePlace; reviews: GoogleLiveReview[] } | null;
  googleOnly: boolean;
}

export function useReviewsModel(): ReviewsModel {
  const orgId = useTenantOrgId();
  const demo = useDemoShowcase();
  const reviewsQuery = useCustomerReviews(orgId);
  const fetched = reviewsQuery.data;
  const googleQuery = usePublicGoogleReviews(orgId);
  const google = googleQuery.data ?? null;
  const pending = isAwaitingFirstResult(reviewsQuery) || isAwaitingFirstResult(googleQuery);

  const reviews: ReviewItem[] = fetched && fetched.length > 0 ? fetched : google ? [] : demo?.testimonials.reviews ?? DEFAULT_REVIEWS;
  const googleOnly = reviews.length === 0 && google?.place.rating != null;
  const average = googleOnly
    ? google!.place.rating!
    : reviews.length > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : 0;

  const items: ReviewCardItem[] = [
    ...(google?.reviews ?? []).map((r) => ({
      key: `g-${r.id}`,
      quote: r.text,
      name: r.authorName,
      meta: r.relativeTime ?? "",
      rating: r.rating,
      photoUrl: r.authorPhotoUrl,
      google: { authorUrl: r.authorUrl, reviewUrl: r.reviewUrl ?? google!.place.googleMapsUri ?? google!.place.reviewsUrl },
    })),
    ...reviews.map((r, i) => ({
      key: `r-${r.id || r.name}-${i}`,
      quote: r.content,
      name: r.name,
      meta: r.location,
      rating: r.rating,
    })),
  ];

  return {
    pending,
    items,
    average,
    label: average >= 4.5 ? "Excellent" : average >= 4 ? "Great" : "Rated",
    countLine: googleOnly
      ? `${google!.place.reviewCount.toLocaleString()} reviews on Google`
      : `${reviews.length} review${reviews.length === 1 ? "" : "s"}`,
    subtitle: demo?.testimonials.subtitle ?? "Approved reviews from real customers",
    google: google ? { place: google.place, reviews: google.reviews } : null,
    googleOnly,
  };
}
